import { inject } from 'inversify';

import {
  CAD_STATE, FIXED_VALUES, SIGNALR, CLIENT_TYPE, ZOLL_PRODUCTS,
} from '@/constants';
import { service } from '@/decorators/service';
import CONSTANTS from '@/helpers/constants';
import { Environment } from '@/services/Environment';
import { useStore as recipientStore } from '@/stores/RecipientStore';
import { assignOwnProperties } from '@/utils/assignOwnProperties';
import { cleanObject } from '@/utils/clean';
import { debugId } from '@/utils/debugId';
import { jwt } from '@/utils/jwt';
import { pathJoin } from '@/utils/pathJoin';
import { postJson } from '@/utils/postJson';
import { INetworkedTenant } from '~/api/INetworkedTenant';

// The interface for data returned by /connect.
export interface IConnectData {
  cadState?: CAD_STATE;
  googleApiKey: string;
  hostName: string;
  redirect: string;
  sessionId: string;
  success: string;
  userRoles: string[];
  networkedTenant?: INetworkedTenant;
  clientType: CLIENT_TYPE,
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type IConnectionLike = Record<any, any> & Partial<IConnectData> | IConnectData | IConnection | Connection;

export interface IIpResponse {
  city: string,
  country: string,
  ip: string,
  loc: string,
  org: string,
  postal: string,
  readme: string,
  region: string,
  timezone: string,
}

// The interface for the Connection instance.
export interface IConnection {
  baseUrl: string;
  cadState: CAD_STATE;
  googleApiKey: string;
  isHybrid: boolean;
  isInitialized: boolean;
  isStandalone: boolean;
  isPortal: boolean;
  redirect: string;
  sessionId: string;
  success: boolean;
  userRoles: string[];
  networkedTenant?: INetworkedTenant;
  clientType: CLIENT_TYPE,
}

export const defaults = <IConnection> {
  baseUrl: '',
  cadState: CAD_STATE.UNINITIALIZED,
  googleApiKey: '',
  isHybrid: false,
  isInitialized: false,
  isStandalone: false,
  isPortal: false,
  redirect: '',
  sessionId: '',
  success: false,
  userRoles: [],
  networkedTenant: {
    tenantKey: '',
    tenantName: '',
    ownerId: '',
    isTerminated: false,
  },
  clientType: CLIENT_TYPE.DISPATCH,
};

export const normalize = (connection?: IConnectionLike): IConnection => {
  const { success, ...cleaned } = cleanObject(connection);

  const normalized = <IConnection> {
    // Merge defaults to ensure all properties exist with default values.
    ...defaults,

    // Merge in cleaned passed values.
    ...cleaned,

    // Indicate successful connection.
    success: success === FIXED_VALUES.TRUE,
  };

  return {
    ...normalized,

    // Provide some boolean flags based on values.
    isHybrid: normalized.clientType === CLIENT_TYPE.DISPATCH && normalized.cadState === CAD_STATE.HYBRID,
    isInitialized: normalized.cadState !== CAD_STATE.UNINITIALIZED,
    isStandalone: normalized.clientType === CLIENT_TYPE.DISPATCH && normalized.cadState === CAD_STATE.STANDALONE,
    isPortal: normalized.clientType === CLIENT_TYPE.PORTAL && normalized.networkedTenant?.tenantKey !== '',
    cadState: (normalized.clientType === CLIENT_TYPE.PORTAL && normalized.networkedTenant?.tenantKey !== '') ? CAD_STATE.PORTAL : normalized.cadState,
  };
};

@service(Connection)
export class Connection implements IConnection {
  // Services.
  @inject(Environment) environment: Environment;

  // Properties.
  readonly baseUrl: string = '';
  readonly cadState: CAD_STATE;
  readonly googleApiKey: string;
  readonly isHybrid: boolean;
  readonly isInitialized: boolean;
  readonly isStandalone: boolean;
  readonly isPortal: boolean;
  readonly redirect: string;
  readonly sessionId: string;
  readonly success: boolean;
  readonly userRoles: string[];
  readonly networkedTenant: INetworkedTenant;
  readonly clientType: CLIENT_TYPE;

  private initialized: boolean = false;

  // Methods.
  async initialize(force = false) {
    if (!force && this.initialized) {
      return;
    }

    const baseUrl = this.environment.signalrBaseUrl;
    let json: IConnectionLike | undefined;
    let provider: INetworkedTenant | undefined;
    const jwtToken = jwt();

    const body = {
      clientType: CLIENT_TYPE.DISPATCH,
      debugId: debugId(),
      jwt: jwtToken,
      providerId: '',
    };

    // Only attempt to establish a connection if there's a JWT token or debugId.
    if (body.jwt || body.debugId) {
      const url = pathJoin(baseUrl, SIGNALR.CONNECT_ENDPOINT);
      if (window.location.pathname.split('/')[1] === ZOLL_PRODUCTS.PORTAL) {
        body.clientType = CLIENT_TYPE.PORTAL;
        const providersUrl = pathJoin(baseUrl, 'getProviders');
        const response = await postJson<INetworkedTenant[]>(providersUrl, { body });
        const providers = response?.filter((tenant) => !tenant.isTerminated) ?? [];
        if (providers.length > 0) {
          provider = providers?.at(0);
          body.providerId = provider?.ownerId ?? '';
        }
        else {
          // No active tenants for this portal user. Complete initialization with portal state
          // so authentication/initialize guards pass, then let AppController redirect to the
          // error page via the router (avoiding a full page reload that would re-run this branch
          // as a dispatch user and send them to login instead of the error page).
          assignOwnProperties(this, normalize({
            baseUrl,
            clientType: body.clientType,
            cadState: CAD_STATE.PORTAL,
            success: FIXED_VALUES.TRUE as string,
            redirect: `/error?code=${CONSTANTS.UI.ERROR_PAGE_CODES.KNOWN.UnauthorizedCompany}`,
          }));
          this.initialized = true;
          return;
        }
      }
      else {
        const recipientsUrl = pathJoin(baseUrl, 'getRecipients');
        const response = await postJson<INetworkedTenant[]>(recipientsUrl, { body });
        if (response && response.length > 0) {
          recipientStore().update(response);
        }
      }

      json = await postJson<IConnectionLike>(url, { body });

      // Older /connect responses do not send the cadState property and was only ever
      // used in a hybrid scenario; which we can safely assume that is true here.
      if (json && json.cadState === undefined) {
        json.cadState = CAD_STATE.HYBRID;
      }
    }

    assignOwnProperties(this, normalize({
      ...json,
      baseUrl,
      networkedTenant: provider,
      clientType: body.clientType,
    }));

    this.initialized = true;
  }

  toJSON(): IConnection {
    return {
      baseUrl: this.baseUrl,
      cadState: this.cadState,
      googleApiKey: this.googleApiKey,
      isHybrid: this.isHybrid,
      isInitialized: this.isInitialized,
      isStandalone: this.isStandalone,
      isPortal: this.isPortal,
      redirect: this.redirect,
      sessionId: this.sessionId,
      success: this.success,
      userRoles: this.userRoles,
      networkedTenant: this.networkedTenant,
      clientType: this.clientType,
    };
  }
}
